import { useCallback, useState } from 'react';
import { useTranslations } from '@/hooks';
import { buildContractLinksPayload } from '@/lib/mappers/links';
import { useGetLinkableContractsQuery, useLinkShipmentContractsMutation } from '@/lib/api/linksApi';
import { LinkContractsModal, LINK_CONTRACT_FLOWS } from './LinkContractsModal';
import { EMPTY_LIST, EMPTY_MAP } from '@/lib/utils';

/**
 * Props:
 *   module        route module segment, for the REST save
 *   shipmentDbId  the shipment's own id, the candidates query's key
 *   shipmentLabel the business shipment id shown in the title
 *   detailFields  (unitType) => GENERAL DETAILS rows
 *   unassignedByUnit  the shipment's still-unassigned amounts, raw, per unit — the assign ceiling
 *   open          whether the wizard is shown
 *   onClose       close handler
 *   onLinked      (links) => void — the shipment's full linked list, straight off the save
 */
export const LinkContractsForShipment = ({
  module,
  shipmentDbId,
  shipmentLabel,
  detailFields,
  unassignedByUnit,
  open,
  onClose,
  onLinked,
}) => {
  const t = useTranslations();
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState(EMPTY_MAP);
  const [linkShipmentContracts, { isLoading }] = useLinkShipmentContractsMutation();
  const { data, isFetching } = useGetLinkableContractsQuery(
    { shipmentId: shipmentDbId, search, filters },
    { skip: !open || shipmentDbId == null },
  );

  const handleClose = useCallback(() => {
    setSearch('');
    setFilters(EMPTY_MAP);
    onClose?.();
  }, [onClose]);

  const handleSave = useCallback(
    async (entries) => {
      const result = await linkShipmentContracts({
        module,
        shipmentId: shipmentDbId,
        links: buildContractLinksPayload(entries),
      });
      if (result.error) return;
      onLinked?.(result.data);
      handleClose();
    },
    [linkShipmentContracts, module, shipmentDbId, handleClose, onLinked],
  );

  // Closing unmounts the modal, so nothing it held survives into the next open.
  if (!open) return null;

  return (
    <LinkContractsModal
      open={open}
      onClose={handleClose}
      title={t('LINK_CONTRACTS_TITLE', { shipmentId: shipmentLabel ?? '' })}
      detailFields={detailFields}
      contracts={data?.rows ?? EMPTY_LIST}
      loading={isFetching}
      flow={LINK_CONTRACT_FLOWS.oneShipment}
      unassignedByUnit={unassignedByUnit}
      isSaving={isLoading}
      onSearchChange={setSearch}
      onFilterChange={setFilters}
      onSave={handleSave}
    />
  );
};
